import { useEffect, useRef, type RefObject } from "react"
import { prefereMenosMovimento } from "@/lib/transicao-view"

// O tempo do Collapse de cada passo, mais uma folga para o CSS terminar de assentar.
const DURACAO_MS = 300
const FOLGA_MS = 120

const suavizar = (t: number): number => 1 - (1 - t) ** 3

function topoNaLista(lista: HTMLElement, alvo: HTMLElement): number {
    return alvo.offsetParent === lista ? alvo.offsetTop : alvo.offsetTop - lista.offsetTop
}

/**
 * Rola a lista até o alvo ficar no topo, ou até o fim dela quando não há mais o que
 * rolar. O destino é relido a cada quadro: na troca de passo o anterior ainda está
 * fechando, e o alvo sobe junto. Devolve como cancelar.
 */
function rolarAte(lista: HTMLElement, alvo: HTMLElement): () => void {
    const respiro = parseFloat(getComputedStyle(lista).paddingTop) || 0
    const destino = (): number => {
        const fim = Math.max(lista.scrollHeight - lista.clientHeight, 0)

        return Math.min(Math.max(topoNaLista(lista, alvo) - respiro, 0), fim)
    }

    if (prefereMenosMovimento()) {
        const espera = window.setTimeout(() => { lista.scrollTop = destino() }, DURACAO_MS + FOLGA_MS)

        return () => window.clearTimeout(espera)
    }

    const partida = lista.scrollTop
    const comeco = performance.now()
    let quadro = 0

    const seguir = (agora: number): void => {
        const decorrido = agora - comeco

        lista.scrollTop = partida + (destino() - partida) * suavizar(Math.min(decorrido / DURACAO_MS, 1))

        if (decorrido < DURACAO_MS + FOLGA_MS) quadro = requestAnimationFrame(seguir)
    }

    // Quem rola com o dedo manda: o acompanhamento não disputa com o cliente.
    const parar = (): void => cancelAnimationFrame(quadro)

    quadro = requestAnimationFrame(seguir)
    lista.addEventListener('touchmove', parar, { passive: true })
    lista.addEventListener('wheel', parar, { passive: true })

    return () => {
        parar()
        lista.removeEventListener('touchmove', parar)
        lista.removeEventListener('wheel', parar)
    }
}

/** Leva o passo ativo para o topo da lista a cada troca de passo. */
export function useAcompanharPasso(indiceAtivo: number, listaNaTela: boolean): RefObject<HTMLDivElement | null> {
    const ref = useRef<HTMLDivElement>(null)

    useEffect(() => {
        const lista = ref.current
        const alvo = lista?.children[indiceAtivo]
        if (!listaNaTela || !lista || !(alvo instanceof HTMLElement)) return

        return rolarAte(lista, alvo)
    }, [indiceAtivo, listaNaTela])

    return ref
}

/** Ao aparecer, rola a lista até o elemento marcado: o que fica no fim não pode passar despercebido. */
export function useMostrarAoAbrir(): { lista: RefObject<HTMLDivElement | null>; alvo: RefObject<HTMLDivElement | null> } {
    const lista = useRef<HTMLDivElement>(null)
    const alvo = useRef<HTMLDivElement>(null)

    useEffect(() => {
        if (!lista.current || !alvo.current) return

        return rolarAte(lista.current, alvo.current)
    }, [])

    return { lista, alvo }
}
